import React, { createContext, useContext, useState } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Function to detect browser language
const getBrowserLanguage = () => {
  const supported = ['tr', 'de', 'en'];
  
  // First check localStorage for saved preference
  if (typeof window !== 'undefined') {
    const savedLang = localStorage.getItem('preferredLanguage');
    if (savedLang && supported.includes(savedLang)) {
      return savedLang;
    }
    
    // Then detect from browser
    if (typeof navigator !== 'undefined') {
      const navLangs = navigator.languages || [navigator.language || navigator.userLanguage];
      for (let lang of navLangs) {
        lang = lang.toLowerCase();
        if (lang.startsWith('tr')) return 'tr';
        if (lang.startsWith('de')) return 'de';
        if (lang.startsWith('en')) return 'en';
      }
    }
  }
  
  // Default to German if no match (since the site is primarily for German market)
  return 'de';
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(getBrowserLanguage());

  const languages = {
    tr: {
      code: 'tr',
      name: 'Türkçe',
      flag: '🇹🇷',
      translations: {
        // Navigation
        'about': 'Hakkımızda',
        'services': 'Hizmetlerimiz',
        'certificate-verify': 'Sertifika Doğrula',
        'add-badge': 'CO₂ Badge Ekle',
        'get-certificate': 'Sertifika Al',
        
        // Hero Section
        'hero-title': 'Karbon Nötr Geleceğe Adım Atın',
        'hero-subtitle': 'CO₂ emisyonlarınızı azaltın, dijital varlıklarınızı nötr hale getirin',
        'hero-title-2': 'Dijital Dünyada Sıfır Karbon',
        'hero-subtitle-2': 'Web sitenizi çevre dostu hale getirin, geleceğe yatırım yapın',
        'hero-title-3': 'Yeşil Teknolojiyle Tanışın',
        'hero-subtitle-3': 'Sürdürülebilir dijital çözümlerle fark yaratın',
        'hero-title-4': 'Gelecek İçin Bugün Harekete Geçin',
        'hero-subtitle-4': 'Karbon ayak izinizi azaltın, doğaya katkı sağlayın',
        'hero-title-5': 'Web Sitenizle Doğayı Korumaya Katılın',
        'hero-subtitle-5': 'CO₂ nötr sertifikanızla çevreye destek olun',
        'hero-title-6': 'Sıfır Emisyon, Sıfır Endişe',
        'hero-subtitle-6': 'Dijital varlıklarınızı karbon nötr hale getirin',
        'hero-title-7': 'Daha Temiz Bir İnternet İçin',
        'hero-subtitle-7': 'Web sitenizi karbon nötr yaparak fark yaratın',
        'hero-title-8': 'Çevre Dostu Dijital Dönüşüm',
        'hero-subtitle-8': 'Sürdürülebilirlik yolculuğunuz burada başlıyor',
        'hero-title-9': 'Karbon Nötr Sertifika ile Güven Verin',
        'hero-subtitle-9': 'Ziyaretçilerinize çevreye duyarlılığınızı gösterin',
        'hero-title-10': 'Dijital Ayak İzinizi Azaltın',
        'hero-subtitle-10': 'Her tıklamada doğaya katkı sağlayın',
        'hero-cta-primary': 'Hemen CO₂ Nötr Ol',
        'hero-cta-secondary': 'Sertifika Al',
        'hero-stats-experience': 'Yıllık Deneyim',
        'hero-stats-sites': 'Nötr Web Sitesi',
        'hero-stats-savings': 'Ton CO₂ Tasarrufu',
        
        // About Section
        'about-title': 'Hakkımızda',
        'about-subtitle': '2008\'den beri Berlin\'de faaliyet gösteren, sürdürülebilir teknoloji çözümleri sunan öncü kuruluş',
        'about-history-title': 'Firma Geçmişi',
        'about-history-text': '2008 yılında Berlin\'de kurulan şirketimiz, dijital dünyanın çevresel etkisini minimize etmek amacıyla yola çıktı. 15 yılı aşkın deneyimimizle, binlerce web sitesini karbon nötr hale getirdik ve sürdürülebilir teknoloji çözümlerinde sektörün öncüsü haline geldik.',
        'about-mission-title': 'Misyonumuz',
        'about-mission-text': 'Dijital varlıkların çevresel etkisini sıfıra indirerek, sürdürülebilir bir gelecek için teknoloji ve çevre arasında köprü kurmak. Her web sitesinin karbon nötr olmasını sağlayarak global iklim hedeflerine katkıda bulunmak.',
        'about-vision-title': 'Vizyonumuz',
        'about-vision-text': 'Tüm dijital platformların karbon nötr olduğu, teknolojinin doğa ile uyum içinde var olduğu bir dünya. İnternetin her byte\'ının çevresel sorumlulukla üretildiği sürdürülebilir bir dijital ekosistem.',
        'about-technologies-title': 'CO₂ Nötr Teknolojiler',
        'about-tech-1': 'Yeşil hosting ve bulut altyapısı',
        'about-tech-2': 'Enerji verimli kod optimizasyonu',
        'about-tech-3': 'Karbon kredisi entegrasyonu',
        'about-tech-4': 'Blockchain destekli şeffaf kayıt sistemi',
        'about-tech-5': 'Gerçek zamanlı emisyon takibi',
        'about-achievement-1-title': 'Gold Standard',
        'about-achievement-1-text': 'Global karbon standartlarına uyum',
        'about-achievement-2-title': 'Verra Sertifikası',
        'about-achievement-2-text': 'Uluslararası karbon doğrulama',
        'about-achievement-3-title': 'ISO 14001',
        'about-achievement-3-text': 'Çevre yönetim sistemi',
        
        // Services
        'services-title': 'Hizmetlerimiz',
        'services-subtitle': 'Web sitenizi karbon nötr hale getirmek için ihtiyacınız olan tüm çözümler',
        'service-co2-web': 'CO₂ Nötr Web Sitesi Hizmeti',
        'service-co2-web-desc': 'Altyapı ve barındırma hizmetlerimizle web sitenizi tamamen karbon nötr hale getirin.',
        'service-co2-web-feature-1': 'Yeşil hosting altyapısı',
        'service-co2-web-feature-2': 'Enerji verimli sunucular',
        'service-co2-web-feature-3': 'Otomatik karbon dengeleme',
        'service-co2-web-feature-4': '7/24 teknik destek',
        'service-calculator': 'Karbon Ayak İzi Hesaplama',
        'service-calculator-desc': 'Web sitenizin gerçek zamanlı karbon emisyonunu hesaplayın ve azaltma stratejileri geliştirin.',
        'service-calculator-feature-1': 'Detaylı emisyon analizi',
        'service-calculator-feature-2': 'Sayfa yükleme süresi optimizasyonu',
        'service-calculator-feature-3': 'Enerji tüketimi raporları',
        'service-calculator-feature-4': 'Karşılaştırmalı performans',
        'service-certificate': 'Karbon Sertifikası Sağlama',
        'service-certificate-desc': 'Uluslararası standartlara uygun, doğrulanabilir karbon nötr sertifikaları alın.',
        'service-certificate-feature-1': 'Gold Standard uyumlu',
        'service-certificate-feature-2': 'Verra doğrulaması',
        'service-certificate-feature-3': 'Blockchain kayıt sistemi',
        'service-certificate-feature-4': 'PDF sertifika indirme',
        'service-exchange': 'Karbon Borsası Bağlantısı',
        'service-exchange-desc': 'Karbon kredilerinizi uluslararası borsalarda satarak ek gelir elde edin.',
        'service-exchange-feature-1': 'Gerçek zamanlı fiyat takibi',
        'service-exchange-feature-2': 'Otomatik alım-satım',
        'service-exchange-feature-3': 'Portföy yönetimi',
        'service-exchange-feature-4': 'Piyasa analizi',
        'services-how-it-works': 'Nasıl Çalışır?',
        'services-step-1-title': 'Analiz',
        'services-step-1-desc': 'Web sitenizin karbon ayak izini hesaplıyoruz',
        'services-step-2-title': 'Optimizasyon',
        'services-step-2-desc': 'Enerji verimliliğini artırıyoruz',
        'services-step-3-title': 'Dengeleme',
        'services-step-3-desc': 'Kalan emisyonları karbon kredileriyle dengeliyoruz',
        'services-step-4-title': 'Sertifika',
        'services-step-4-desc': 'Karbon nötr sertifikanızı alıyorsunuz',
        
        // Certificate Verification
        'verification-title': 'Sertifika Doğrulama Sistemi',
        'verification-subtitle': 'Sertifika numaranızı girerek geçerlilik kontrolü yapabilirsiniz',
        'verification-placeholder': 'Sertifika kodu girin (örn: CO2N-2024-001)',
        'verification-button': 'Doğrula',
        'verification-loading': 'Doğrulanıyor...',
        'verification-valid': 'Sertifika Geçerli',
        'verification-invalid': 'Sertifika Geçersiz',
        
        // Add Badge
        'badge-title': 'CO₂ Neutral Rozeti Ekle',
        'badge-subtitle': 'Web sitenize özelleştirilebilir CO₂ neutral rozeti ekleyin',
        'badge-language': 'Dil Seçimi',
        'badge-text': 'Bu site karbon nötrdür',
        
        // Footer
        'footer-company-desc': 'We provide leading sustainable technology solutions.',
        'footer-carbon-neutral': 'Bu site karbon nötrdür',
        'footer-certificate-no': 'Sertifika No: CO2N-2024-001',
        'footer-quick-links': 'Hızlı Menüler',
        'footer-contact': 'İletişim',
        'footer-address': 'Berlin, Almanya',
        'footer-social-media': 'Sosyal Medya',
        'footer-copyright': '© 2025 CO2 Neutral. Tüm hakları saklıdır.',
        'footer-privacy': 'Gizlilik Politikası',
        'footer-cookies': 'Çerez Politikası',
        'footer-terms': 'Kullanım Şartları',
        'why-us-title': 'Neden Biz?',
        
        // Contact Page
        'contact-page-title': 'İletişim',
        'contact-page-subtitle': 'Bizimle iletişime geçin. Size yardımcı olmaktan mutluluk duyarız.',
        'contact-form-title': 'Mesaj Gönder',
        'contact-form-name': 'Ad Soyad',
        'contact-form-name-placeholder': 'Adınız ve soyadınız',
        'contact-form-email': 'E-posta',
        'contact-form-email-placeholder': 'E-posta adresiniz',
        'contact-form-subject': 'Konu',
        'contact-form-subject-placeholder': 'Mesajınızın konusu',
        'contact-form-message': 'Mesaj',
        'contact-form-message-placeholder': 'Mesajınızı buraya yazın...',
        'contact-form-submit': 'Mesaj Gönder',
        'contact-success-title': 'Mesajınız Gönderildi!',
        'contact-success-message': 'En kısa sürede size geri dönüş yapacağız.',
        'contact-new-message': 'Yeni Mesaj',
        'contact-business-hours': 'Çalışma Saatleri',
        'contact-monday-friday': 'Pazartesi - Cuma',
        'contact-saturday': 'Cumartesi',
        'contact-sunday': 'Pazar',
        'contact-closed': 'Kapalı',
        'contact-quick-contact': 'Hızlı İletişim',
        'contact-quick-contact-desc': 'Acil durumlar için bizi arayabilir veya e-posta gönderebilirsiniz.',
        'contact-map-title': 'Konum',
        'contact-map-placeholder': 'Harita yükleniyor...',
        
        // Why Us Section
        'why-us-subtitle': '15 yıllık deneyimimiz ve binlerce başarılı projemizle sürdürülebilir teknoloji çözümlerinde öncüyüz',
        'why-us-stats-customers': 'Mutlu Müşteri',
        'why-us-stats-certificates': 'Sertifika',
        'why-us-stats-countries': 'Ülke',
        'why-us-stats-rating': 'Müşteri Puanı',
        'why-us-testimonials-title': 'Müşteri Yorumları',
        'testimonial-1-name': 'Ahmet Yılmaz',
        'testimonial-1-company': 'TechCorp GmbH',
        'testimonial-1-role': 'CEO',
        'testimonial-1-content': 'CO2 Neutral ile web sitemizi karbon nötr hale getirdik. Müşterilerimiz artık çevre dostu bir şirketle çalıştıklarını biliyor ve bu bize güven veriyor.',
        'testimonial-2-name': 'Maria Schmidt',
        'testimonial-2-company': 'GreenStart Solutions',
        'testimonial-2-role': 'Marketing Director',
        'testimonial-2-content': 'Sertifika süreci çok kolaydı. Teknik ekip her adımda bize yardımcı oldu ve sonuçta harika bir karbon nötr web sitemiz oldu.',
        'testimonial-3-name': 'Mehmet Kaya',
        'testimonial-3-company': 'EcoTech Industries',
        'testimonial-3-role': 'CTO',
        'testimonial-3-content': 'Blockchain destekli şeffaf sistem sayesinde müşterilerimiz sertifikamızı kolayca doğrulayabiliyor. Bu güven oluşturuyor.',
        'why-us-standards-title': 'Global Karbon Standartlarına Uyum',
        'standard-1-title': 'Gold Standard',
        'standard-1-desc': 'En yüksek kalitede karbon kredisi standardı',
        'standard-1-status': 'Uyumlu',
        'standard-2-title': 'Verra VCS',
        'standard-2-desc': 'Voluntary Carbon Standard doğrulaması',
        'standard-2-status': 'Onaylı',
        'standard-3-title': 'ISO 14001',
        'standard-3-desc': 'Çevre yönetim sistemi',
        'standard-3-status': 'Sertifikalı',
        'why-us-blockchain-title': 'Blockchain Destekli Şeffaf Kayıt Sistemi',
        'why-us-blockchain-desc': 'Tüm karbon dengeleme işlemlerimiz blockchain teknolojisi ile kayıt altına alınır. Bu sayede her işlem şeffaf, doğrulanabilir ve değiştirilemezdir.',
        'why-us-blockchain-feature-1': 'Gerçek zamanlı takip',
        'why-us-blockchain-feature-2': 'Değiştirilemez kayıtlar',
        'why-us-blockchain-feature-3': 'Kamuya açık doğrulama',
        'why-us-recent-transactions': 'Son İşlemler',
        
        // Certificate Form
        'form-subtitle': 'Web sitenizi karbon nötr hale getirin ve sertifikanızı alın',
        'form-application-type': 'Başvuru Türü',
        'form-type-personal': 'Kişisel',
        'form-type-personal-desc': 'Bireysel web siteleri',
        'form-type-corporate': 'Kurumsal',
        'form-type-corporate-desc': 'Şirket web siteleri',
        'form-name': 'Ad Soyad',
        'form-authority-person': 'Yetkili Kişi',
        'form-name-placeholder': 'Adınız ve soyadınız',
        'form-authority-placeholder': 'Yetkili kişi adı',
        'form-email': 'E-posta',
        'form-company-name': 'Şirket Adı',
        'form-company-placeholder': 'Şirket adınız',
        'form-sector': 'Sektör',
        'form-sector-select': 'Sektör seçin',
        'form-sector-tech': 'Teknoloji',
        'form-sector-ecommerce': 'E-ticaret',
        'form-sector-finance': 'Finans',
        'form-sector-health': 'Sağlık',
        'form-sector-education': 'Eğitim',
        'form-sector-tourism': 'Turizm',
        'form-sector-media': 'Medya',
        'form-sector-other': 'Diğer',
        'form-website': 'Web Sitesi Adresi',
        'form-phone': 'Telefon',
        'form-message': 'Ek Notlar',
        'form-message-placeholder': 'Ek bilgiler, özel istekler veya sorularınız...',
        'form-submitting': 'Gönderiliyor...',
        'form-submit': 'Başvuruyu Gönder',
        'form-success-title': 'Başvurunuz Alındı!',
        'form-success-message': 'Sertifika başvurunuz başarıyla kaydedildi. Uzman ekibimiz en kısa sürede sizinle iletişime geçecek ve süreç hakkında detaylı bilgi verecektir.',
        'form-next-steps': 'Sonraki Adımlar',
        'form-step-1': 'E-posta ile onay mesajı alacaksınız',
        'form-step-2': '24 saat içinde uzmanımız sizi arayacak',
        'form-step-3': 'Web sitenizin analizi yapılacak',
        'form-step-4': 'Karbon nötr süreç başlatılacak',
        'form-new-application': 'Yeni Başvuru',
        'form-benefits-title': 'Faydalar',
        'form-benefit-1-title': 'Çevre Dostu İmaj',
        'form-benefit-1-desc': 'Müşterilerinize sürdürülebilirlik taahhüdünüzü gösterin',
        'form-benefit-2-title': 'SEO Avantajı',
        'form-benefit-2-desc': 'Arama motorlarında daha iyi sıralama elde edin',
        'form-benefit-3-title': 'Maliyet Tasarrufu',
        'form-benefit-3-desc': 'Enerji verimli altyapı ile tasarruf sağlayın',
        'form-process-title': 'Süreç',
        'form-process-1': 'Karbon ayak izi analizi',
        'form-process-2': 'Yeşil hosting kurulumu',
        'form-process-3': 'Karbon dengeleme',
        'form-process-4': 'Sertifika teslimi',
        
        // Video Section
        'video-title': 'CO₂ Nötr Olmak Neden Önemlidir?',
        'video-subtitle': 'Dijital dünyanın çevresel etkisini anlayın ve neden karbon nötr olmanın kritik olduğunu keşfedin',
        'video-video-title': 'CO₂ Nötr Web Siteleri',
        'video-duration': '3:45 dakika',
        'video-quality': 'HD Kalite',
        'video-content-title': 'Dijital Dünyanın Gizli Karbon Ayak İzi',
        'video-content-desc': 'İnternet, dünyanın en büyük elektrik tüketicilerinden biridir. Her web sitesi ziyareti, her e-posta gönderimi, her arama sorgusu karbon emisyonuna neden olur.',
        'video-stat-1': 'Global CO₂ Emisyonu',
        'video-stat-2': 'Ton Tasarruf',
        'video-stat-3': 'Enerji Tasarrufu',
        
        // Tree Calculator
        'tree-calculator-title': 'Ormana kaç ağaç dikmek istiyorsunuz?',
        'tree-calculator-slider': 'Kaydırıcı',
        'tree-calculator-co2': 'CO2 Miktarı',
        'tree-calculator-visitor': 'Ziyaretçi',
        'tree-calculator-samples': 'Örnekler',
        'tree-calculator-trees': 'Ağaç',
        'tree-calculator-co2-placeholder': 'CO₂ (ton)',
        'tree-calculator-visitor-placeholder': 'Aylık Ziyaretçi Sayısı',
        'tree-calculator-visitor-note': 'Her 1000 ziyaretçi için 1 ağaç önerilir.',
        'tree-calculator-sample-1': '• 1 uçak bileti (İstanbul-Berlin): 2 ağaç',
        'tree-calculator-sample-2': '• 1 yıl ortalama web sitesi: 5 ağaç',
        'tree-calculator-sample-3': '• 1 dizüstü bilgisayar: 3 ağaç',
        'tree-calculator-unit-price': 'Birim Fiyat €8/Ağaç',
        'tree-calculator-cta': 'HEMEN NEFES OL',
        
        // Visitor Boost
        'visitor-boost-title': 'Siteniz Karbon Nötr Olsun,',
        'visitor-boost-increase': '%30+ Ziyaretçi Artışı',
        'visitor-boost-achieve': 'Sağlayın!',
        'visitor-boost-description': 'Karbon nötr web siteleri, çevreye duyarlı kullanıcıların ilgisini çeker ve %30\'dan fazla ziyaretçi artışı sağlar. Hemen karbon nötr olun, hem doğaya hem işinize katkı sağlayın!',
        
        // Big Brands Zero Carbon
        'big-brands-title': 'Büyük Markalar 0 Karbon Ayak İzi Yolunda',
        'big-brands-subtitle': 'Dünyanın en büyük şirketleri karbon nötr olmayı seçiyor. Sürdürülebilirlikte öncü olan bu markalar, dijital dünyada örnek oluyor.',
        'brand-google-desc': 'Google, 2007\'den beri karbon nötr ve 2030\'a kadar tamamen karbon ayak izini sıfırlamayı hedefliyor.',
        'brand-microsoft-desc': 'Microsoft, 2012\'den beri karbon nötr ve 2030\'da karbon negatif olmayı taahhüt etti.',
        'brand-amazon-desc': 'Amazon, 2040\'a kadar tüm operasyonlarında net sıfır karbon hedefliyor ve devasa yenilenebilir enerji yatırımları yapıyor.',
        'brand-tesla-desc': 'Tesla, elektrikli araç ve enerji çözümleriyle karbon ayak izini minimuma indirmeyi amaçlıyor.',
        'brand-visit-sustainability': 'Sürdürülebilirlik sayfasını ziyaret et →',
        'big-brands-cta': 'Sürdürülebilirlikte lider olmak için siz de karbon nötr olun!',
        
        // Certificate Verification
        'cert-verification-title': 'Sertifika Doğrulama Sistemi',
        'cert-verification-subtitle': 'Sertifika numaranızı girerek geçerlilik kontrolü yapabilirsiniz',
        'cert-verification-placeholder': 'Sertifika kodu girin (örn: CO2N-2024-001)',
        'cert-verification-verify': 'Doğrula',
        'cert-verification-verifying': 'Doğrulanıyor...',
        'cert-verification-valid': 'Sertifika Geçerli',
        'cert-verification-invalid': 'Sertifika Geçersiz',
        'cert-verification-company': 'Şirket Adı',
        'cert-verification-website': 'Web Sitesi',
        'cert-verification-issued-date': 'Veriliş Tarihi',
        'cert-verification-expiry-date': 'Geçerlilik Tarihi',
        'cert-verification-carbon-offset': 'Karbon Dengeleme',
        'cert-verification-certificate-id': 'Sertifika ID',
        'cert-verification-download-pdf': 'PDF İndir',
        'cert-verification-blockchain-verified': 'Blockchain doğrulaması yapıldı',
        'cert-verification-example-codes': 'Örnek Sertifika Kodları',
        'cert-verification-valid-cert': 'Geçerli Sertifika',
        'cert-verification-enter-code': 'Lütfen bir sertifika kodu girin',
        'cert-verification-invalid-code': 'Geçersiz sertifika kodu',
        'cert-verification-not-found': 'Sertifika bulunamadı',
        'cert-truja-carbon-offset': 'Karbon nötr sertifika',
        'cert-neckar-carbon-offset': '250 ağaç dikildi',
        'cert-ish-carbon-offset': '12.000 ton CO₂ nötrleştirildi',
        'cert-enucuzfly-carbon-offset': '850 ton CO₂ nötralize edildi',
        
        // Global Partners
        'global-partners-title': 'Global Karbon Ayak İzi Partnerlerimiz',
        
        // Certificate Verification Page
        'cert-page-title': 'Sertifika Doğrulama Sistemi',
        'cert-page-subtitle': 'Sertifika numaranızı girerek geçerlilik kontrolü yapabilirsiniz. Blockchain destekli güvenli doğrulama sistemi ile sertifikalarınızı anında kontrol edin.',
        'cert-page-stats-verified': 'Doğrulanan Sertifika',
        'cert-page-stats-accuracy': 'Doğruluk Oranı',
        'cert-page-stats-time': 'Ortalama Süre',
        'cert-page-verify-title': 'Sertifika Doğrula',
        'cert-page-valid-message': 'Bu sertifika aktif ve geçerlidir',
        'cert-page-status': 'Durum',
        'cert-page-verification-count': 'Doğrulama Sayısı',
        'cert-page-features-title': 'Özellikler',
        'cert-page-feature-1-title': 'Blockchain Güvenliği',
        'cert-page-feature-1-desc': 'Tüm sertifikalar blockchain üzerinde güvenli şekilde saklanır',
        'cert-page-feature-2-title': 'Anında Doğrulama',
        'cert-page-feature-2-desc': 'Sertifika geçerliliği anında kontrol edilir',
        'cert-page-feature-3-title': 'Global Erişim',
        'cert-page-feature-3-desc': 'Dünya çapında erişilebilir doğrulama sistemi',
        'cert-page-recent-title': 'Son Doğrulamalar',
        'cert-page-recent-verified': 'Doğrulandı',
        'cert-page-status-active': 'Aktif',
        'cert-pdf-certificate-of': 'Sertifika',
        'cert-pdf-footer-note': 'Bu sertifika blockchain teknolojisi ile doğrulanmıştır ve geçerliliği online olarak kontrol edilebilir.',
        
        // CO2 Badge Page
        'badge-page-title': 'CO₂ Neutral Rozeti Ekle',
        'badge-page-subtitle': 'Web sitenize özelleştirilebilir CO₂ neutral rozeti ekleyin. Renk, font ve boyut seçenekleriyle sitenize uygun hale getirin.',
        'badge-customization-title': 'Rozet Özelleştirme',
        'badge-language-selection': 'Dil Seçimi',
        'badge-selected-language': 'Seçilen dil',
        'badge-background-color': 'Arkaplan Rengi',
        'badge-text-color': 'Metin Rengi',
        'badge-font-family': 'Font Ailesi',
        'badge-font-size': 'Font Boyutu',
        'badge-border-radius': 'Köşe Yuvarlaklığı',
        'badge-padding': 'İç Boşluk',
        'badge-show-icon': 'İkon Göster',
        'badge-icon-color': 'İkon Rengi',
        'badge-purchase-button': 'Rozeti Satın Al',
        'badge-preview-title': 'Canlı Önizleme',
        'badge-preview-feature-1': 'Responsive tasarım',
        'badge-preview-feature-2': 'SEO dostu',
        'badge-preview-feature-3': 'Hızlı yükleme',
        'badge-code-title': 'Kod Çıktısı',
        'badge-css-code': 'CSS Kodu',
        'badge-html-code': 'HTML Kodu',
        'badge-features-title': 'Özellikler',
        'badge-feature-1': 'Kolay entegrasyon',
        'badge-feature-2': 'Tam özelleştirme',
        'badge-feature-3': 'Anında indirme',
        'badge-feature-4': 'Profesyonel görünüm',
        
        // Cart and Checkout
        'add-to-cart': 'Sepete Ekle',
        'cart-hide': 'Gizle',
        'cart-show': 'Göster',
        'cart-items': 'Sepet Öğeleri',
        'cart-total': 'Toplam',
        'proceed-to-checkout': 'Ödemeye Geç',
        'checkout-title': 'Ödeme',
        'checkout-no-order': 'Sipariş bulunamadı',
        'back-to-home': 'Ana Sayfaya Dön',
        'order-summary': 'Sipariş Özeti',
        'order-id': 'Sipariş No',
        'total-amount': 'Toplam Tutar',
        'payment-method': 'Ödeme Yöntemi',
        'bank-transfer': 'Banka Havalesi',
        'bank-details': 'Banka Bilgileri',
        'iban': 'IBAN',
        'bic': 'BIC',
        'account-holder': 'Hesap Sahibi',
        'address': 'Adres',
        'reference': 'Referans',
        'important-notes': 'Önemli Notlar',
        'note-1': 'Ödeme yaparken referans numarasını belirtmeyi unutmayın',
        'note-2': 'Ödeme 1-3 iş günü içinde işleme alınacaktır',
        'note-3': 'Sipariş onaylandıktan sonra e-posta ile bilgilendirileceksiniz',
        'confirm-order': 'Siparişi Onayla',
        'order-confirmed': 'Sipariş Onaylandı!',
        'order-confirmed-desc': 'Siparişiniz başarıyla alındı. Ödeme bilgileri aşağıda yer almaktadır.',
        'payment-instructions': 'Ödeme Talimatları',
        'download-invoice': 'Fatura İndir',
        
        // Live Chat Widget
        'chat-title': 'Canlı Destek',
        'chat-placeholder': 'Mesajınızı yazın...',
        'chat-greeting': 'Merhaba! Size nasıl yardımcı olabilirim?',
        'chat-reply-1': 'Size bu konuda yardımcı olabilirim!',
        'chat-reply-2': 'Sorunuzu detaylandırabilir misiniz?',
        'chat-reply-3': 'En kısa sürede size dönüş yapacağım.',
        'chat-reply-4': 'Daha fazla bilgi almak ister misiniz?',
        'chat-ai-assistant': 'Yapay Zeka Asistanı',
      }
    },
    de: {
      code: 'de',
      name: 'Deutsch',
      flag: '🇩🇪',
      translations: {
        // Navigation
        'about': 'Über uns',
        'services': 'Dienstleistungen',
        'certificate-verify': 'Zertifikat verifizieren',
        'add-badge': 'CO₂ Badge hinzufügen',
        'get-certificate': 'Zertifikat erhalten',
        
        // Hero Section
        'hero-title': 'Schritt in eine CO₂-neutrale Zukunft',
        'hero-subtitle': 'Reduzieren Sie Ihre CO₂-Emissionen, machen Sie Ihre digitalen Assets neutral',
        'hero-title-2': 'Null CO₂ im digitalen Zeitalter',
        'hero-subtitle-2': 'Machen Sie Ihre Website umweltfreundlich und investieren Sie in die Zukunft',
        'hero-title-3': 'Treten Sie mit nachhaltigen Technologien in Berührung',
        'hero-subtitle-3': 'Erzeugen Sie einen Unterschied mit nachhaltigen digitalen Lösungen',
        'hero-title-4': 'Beginnen Sie heute mit dem Handeln für die Zukunft',
        'hero-subtitle-4': 'Reduzieren Sie Ihren CO₂ Fußabdruck und tragen Sie zum Umweltschutz bei',
        'hero-title-5': 'Treten Sie mit Ihrer Website für eine bessere Umwelt ein',
        'hero-subtitle-5': 'Unterstützen Sie die Umwelt mit Ihrem CO₂-neutralen Zertifikat',
        'hero-title-6': 'Null Emission, Null Sorge',
        'hero-subtitle-6': 'Machen Sie Ihre digitalen Assets neutral',
        'hero-title-7': 'Für einen saubereren Internet',
        'hero-subtitle-7': 'Machen Sie Ihre Website kohlenstoffneutral und erzeugen Sie einen Unterschied',
        'hero-title-8': 'Umweltfreundliche digitale Transformation',
        'hero-subtitle-8': 'Ihr nachhaltiges Reisen beginnt hier',
        'hero-title-9': 'Vertrauen Sie auf ein CO₂-neutrales Zertifikat',
        'hero-subtitle-9': 'Zeigen Sie Ihre Besucher Ihre Umweltbewusstsein',
        'hero-title-10': 'Reduzieren Sie Ihren digitalen Fußabdruck',
        'hero-subtitle-10': 'Tragen Sie jeden Klick für die Umwelt bei',
        'hero-cta-primary': 'Jetzt CO₂-neutral werden',
        'hero-cta-secondary': 'Zertifikat erhalten',
        'hero-stats-experience': 'Jahre Erfahrung',
        'hero-stats-sites': 'Kohlenstoffneutrale Websites',
        'hero-stats-savings': 'CO₂-Einsparungen in Tonnen',
        
        // About Section
        'about-title': 'Über uns',
        'about-subtitle': 'Seit 2008 in Berlin tätig, bieten wir führende nachhaltige Technologielösungen an.',
        'about-history-title': 'Firmengeschichte',
        'about-history-text': 'Unser Unternehmen wurde 2008 in Berlin gegründet, um die Umweltbelastung des digitalen Zeitalters zu minimieren. Mit über 15 Jahren Erfahrung haben wir Tausende von Websites auf CO₂-neutralität gebracht und sind zum führenden Unternehmen in nachhaltigen Technologielösungen geworden.',
        'about-mission-title': 'Unser Ziel',
        'about-mission-text': 'Wir schlagen vor, die Umweltbelastung der digitalen Assets auf Null zu reduzieren, um eine nachhaltige Zukunft zu schaffen. Durch die Sicherstellung, dass jede Website CO₂-neutral ist, tragen wir zum Erreichen der globalen Klimaziele bei.',
        'about-vision-title': 'Unser Vision',
        'about-vision-text': 'Eine Welt, in der alle digitalen Plattformen CO₂-neutral sind, und Technologie in Einklang mit der Natur steht. Eine nachhaltige digitale Ökosystem, in dem jeder Byte des Internets mit Umweltsorgen produziert wird.',
        'about-technologies-title': 'CO₂-neutrale Technologien',
        'about-tech-1': 'Nachhaltige Hosting- und Cloud-Infrastruktur',
        'about-tech-2': 'Energieeffiziente Code-Optimierung',
        'about-tech-3': 'Kohlenstoffkredit-Integration',
        'about-tech-4': 'Blockchain-unterstütztes transparentes Registrierungssystem',
        'about-tech-5': 'Echtzeit-Emissionen-Tracking',
        'about-achievement-1-title': 'Gold Standard',
        'about-achievement-1-text': 'Konformität mit globalen Kohlenstoffstandards',
        'about-achievement-2-title': 'Verra-Zertifikat',
        'about-achievement-2-text': 'Internationale Kohlenstoffverifizierung',
        'about-achievement-3-title': 'ISO 14001',
        'about-achievement-3-text': 'Umweltmanagementsystem',
        
        // Services
        'services-title': 'Unsere Dienstleistungen',
        'services-subtitle': 'Alle Lösungen, die Ihre Website zu einem CO₂-neutralen Web erweitern',
        'service-co2-web': 'CO₂-neutrale Website-Dienste',
        'service-co2-web-desc': 'Mit unseren Infrastruktur- und Hosting-Diensten bringen wir Ihre Website vollständig zu CO₂-Neutralität.',
        'service-co2-web-feature-1': 'Nachhaltige Hosting-Infrastruktur',
        'service-co2-web-feature-2': 'Energieeffiziente Server',
        'service-co2-web-feature-3': 'Automatische Kohlenstoffausgleich',
        'service-co2-web-feature-4': '24/7 technische Unterstützung',
        'service-calculator': 'CO₂-Fußabdruck-Berechnung',
        'service-calculator-desc': 'Berechnen Sie Ihren tatsächlichen CO₂-Fußabdruck und entwickeln Sie Strategien zur Reduzierung.',
        'service-calculator-feature-1': 'Detaillierte Emissionsanalyse',
        'service-calculator-feature-2': 'Optimierung der Seitenladezeit',
        'service-calculator-feature-3': 'Energieverbrauchsberichte',
        'service-calculator-feature-4': 'Vergleichbare Leistung',
        'service-certificate': 'CO₂-Zertifizierung',
        'service-certificate-desc': 'Holen Sie sich CO₂-neutrale Zertifikate, die den internationalen Standards entsprechen und überprüfbar sind.',
        'service-certificate-feature-1': 'Gold Standard-Konformität',
        'service-certificate-feature-2': 'Verra-Verifizierung',
        'service-certificate-feature-3': 'Blockchain-Registrierungssystem',
        'service-certificate-feature-4': 'PDF-Zertifikat herunterladen',
        'service-exchange': 'CO₂-Börsenverbindung',
        'service-exchange-desc': 'Erhalten Sie zusätzlichen Einkommen, indem Sie Ihre Kohlenstoffkredite auf internationalen Börsen handeln.',
        'service-exchange-feature-1': 'Echtzeit-Preisverfolgung',
        'service-exchange-feature-2': 'Automatisiertes Kauf- und Verkauf',
        'service-exchange-feature-3': 'Portfolio-Verwaltung',
        'service-exchange-feature-4': 'Marktanalyse',
        'services-how-it-works': 'Wie funktioniert es?',
        'services-step-1-title': 'Analyse',
        'services-step-1-desc': 'Wir analysieren Ihren CO₂-Fußabdruck',
        'services-step-2-title': 'Optimierung',
        'services-step-2-desc': 'Wir erhöhen die Energieeffizienz',
        'services-step-3-title': 'Ausgleich',
        'services-step-3-desc': 'Wir balancieren die verbleibenden Emissionen mit Kohlenstoffkrediten',
        'services-step-4-title': 'Zertifikat',
        'services-step-4-desc': 'Sie erhalten Ihr CO₂-neutrales Zertifikat',
        
        // Certificate Verification
        'verification-title': 'Zertifikatsverifizierungssystem',
        'verification-subtitle': 'Überprüfen Sie die Gültigkeit durch Eingabe Ihrer Zertifikatsnummer',
        'verification-placeholder': 'Zertifikatscode eingeben (z.B.: CO2N-2024-001)',
        'verification-button': 'Verifizieren',
        'verification-loading': 'Wird verifiziert...',
        'verification-valid': 'Zertifikat gültig',
        'verification-invalid': 'Zertifikat ungültig',
        'cert-verification-title': 'Zertifikatsverifizierungssystem',
        'cert-verification-subtitle': 'Überprüfen Sie die Gültigkeit durch Eingabe Ihrer Zertifikatsnummer',
        'cert-verification-placeholder': 'Zertifikatscode eingeben (z.B.: CO2N-2024-001)',
        'cert-verification-verify': 'Verifizieren',
        'cert-verification-verifying': 'Wird verifiziert...',
        'cert-verification-valid': 'Zertifikat gültig',
        'cert-verification-invalid': 'Zertifikat ungültig',
        'cert-verification-company': 'Firmenname',
        'cert-verification-website': 'Website',
        'cert-verification-issued-date': 'Ausstellungsdatum',
        'cert-verification-expiry-date': 'Ablaufdatum',
        'cert-verification-carbon-offset': 'CO₂-Ausgleich',
        'cert-verification-certificate-id': 'Zertifikats-ID',
        'cert-verification-download-pdf': 'PDF herunterladen',
        'cert-verification-blockchain-verified': 'Blockchain-Verifizierung durchgeführt',
        'cert-verification-example-codes': 'Beispiel-Zertifikatscodes',
        'cert-verification-valid-cert': 'Gültiges Zertifikat',
        'cert-verification-enter-code': 'Bitte geben Sie einen Zertifikatscode ein',
        'cert-verification-invalid-code': 'Ungültiger Zertifikatscode',
        'cert-verification-not-found': 'Zertifikat nicht gefunden',
        'cert-truja-carbon-offset': 'CO₂-neutrales Zertifikat',
        'cert-neckar-carbon-offset': '250 Bäume gepflanzt',
        'cert-ish-carbon-offset': '12.000 Tonnen CO₂ neutralisiert',
        'cert-enucuzfly-carbon-offset': '850 Tonnen CO₂ neutralisiert',
        'global-partners-title': 'Unsere globalen CO₂-Fußabdruck-Partner',
        
        // Certificate Verification Page
        'cert-page-title': 'Zertifikatsverifizierungssystem',
        'cert-page-subtitle': 'Überprüfen Sie die Gültigkeit durch Eingabe Ihrer Zertifikatsnummer. Blockchain-unterstütztes sicheres Verifizierungssystem zur sofortigen Überprüfung Ihrer Zertifikate.',
        'cert-page-stats-verified': 'Verifizierte Zertifikate',
        'cert-page-stats-accuracy': 'Genauigkeitsrate',
        'cert-page-stats-time': 'Durchschnittliche Zeit',
        'cert-page-verify-title': 'Zertifikat verifizieren',
        'cert-page-valid-message': 'Dieses Zertifikat ist aktiv und gültig',
        'cert-page-status': 'Status',
        'cert-page-verification-count': 'Anzahl der Verifizierungen',
        'cert-page-features-title': 'Funktionen',
        'cert-page-feature-1-title': 'Blockchain-Sicherheit',
        'cert-page-feature-1-desc': 'Alle Zertifikate werden sicher auf der Blockchain gespeichert',
        'cert-page-feature-2-title': 'Sofortige Verifizierung',
        'cert-page-feature-2-desc': 'Die Zertifikatsgültigkeit wird sofort überprüft',
        'cert-page-feature-3-title': 'Globaler Zugang',
        'cert-page-feature-3-desc': 'Weltweit zugängliches Verifizierungssystem',
        'cert-page-recent-title': 'Letzte Verifizierungen',
        'cert-page-recent-verified': 'Verifiziert',
        'cert-page-status-active': 'Aktiv',
        'cert-pdf-certificate-of': 'Zertifikat',
        'cert-pdf-footer-note': 'Dieses Zertifikat wurde mit Blockchain-Technologie verifiziert und kann online auf Gültigkeit überprüft werden.',
        
        // Add Badge
        'badge-title': 'CO₂-neutrales Badge hinzufügen',
        'badge-subtitle': 'Fügen Sie Ihrer Website ein anpassbares CO₂-neutrales Badge hinzu',
        'badge-language': 'Sprachauswahl',
        'badge-text': 'Diese Website ist CO₂-neutral',
        
        // Footer
        'footer-company-desc': 'Sürdürülebilir teknoloji çözümleri sunan öncü kuruluş.',
        'footer-carbon-neutral': 'Diese Website ist CO₂-neutral',
        'footer-certificate-no': 'Zertifikat Nr.: CO2N-2024-001',
        'footer-quick-links': 'Schnelllinks',
        'footer-contact': 'Kontakt',
        'footer-address': 'Berlin, Deutschland',
        'footer-social-media': 'Soziale Medien',
        'footer-copyright': '© 2025 CO2 Neutral. Alle Rechte vorbehalten.',
        'footer-privacy': 'Datenschutzrichtlinie',
        'footer-cookies': 'Cookie-Richtlinie',
        'footer-terms': 'Nutzungsbedingungen',
        'why-us-title': 'Warum wir?',
        
        // Contact Page
        'contact-page-title': 'Kontakt',
        'contact-page-subtitle': 'Kontaktieren Sie uns. Wir helfen Ihnen gerne weiter.',
        'contact-form-title': 'Nachricht senden',
        'contact-form-name': 'Vor- und Nachname',
        'contact-form-name-placeholder': 'Ihr Vor- und Nachname',
        'contact-form-email': 'E-Mail',
        'contact-form-email-placeholder': 'Ihre E-Mail-Adresse',
        'contact-form-subject': 'Betreff',
        'contact-form-subject-placeholder': 'Betreff Ihrer Nachricht',
        'contact-form-message': 'Nachricht',
        'contact-form-message-placeholder': 'Schreiben Sie Ihre Nachricht hier...',
        'contact-form-submit': 'Nachricht senden',
        'contact-success-title': 'Ihre Nachricht wurde gesendet!',
        'contact-success-message': 'Wir werden uns schnellstmöglich bei Ihnen melden.',
        'contact-new-message': 'Neue Nachricht',
        'contact-business-hours': 'Geschäftszeiten',
        'contact-monday-friday': 'Montag - Freitag',
        'contact-saturday': 'Samstag',
        'contact-sunday': 'Sonntag',
        'contact-closed': 'Geschlossen',
        'contact-quick-contact': 'Schneller Kontakt',
        'contact-quick-contact-desc': 'Für Notfälle können Sie uns anrufen oder eine E-Mail senden.',
        'contact-map-title': 'Standort',
        'contact-map-placeholder': 'Karte wird geladen...',
        
        // Why Us Section
        'why-us-subtitle': '15 Jahre Erfahrung und tausende erfolgreiche Projekte machen uns zum führenden Unternehmen in nachhaltigen Technologielösungen.',
        'why-us-stats-customers': 'Zufriedene Kunden',
        'why-us-stats-certificates': 'Zertifikate',
        'why-us-stats-countries': 'Länder',
        'why-us-stats-rating': 'Kundenbewertung',
        'why-us-testimonials-title': 'Kundenrezensionen',
        'testimonial-1-name': 'Ahmet Yılmaz',
        'testimonial-1-company': 'TechCorp GmbH',
        'testimonial-1-role': 'CEO',
        'testimonial-1-content': 'Mit CO2 Neutral haben wir unsere Website zu einem kohlenstoffneutralen Web gemacht. Unsere Kunden wissen jetzt, dass sie mit einer umweltfreundlichen Firma zusammenarbeiten, und das gibt uns Vertrauen.',
        'testimonial-2-name': 'Maria Schmidt',
        'testimonial-2-company': 'GreenStart Solutions',
        'testimonial-2-role': 'Marketing-Direktor',
        'testimonial-2-content': 'Der Zertifizierungsprozess war sehr einfach. Das technische Team half uns bei jedem Schritt und am Ende hatten wir eine fantastische kohlenstoffneutrale Website.',
        'testimonial-3-name': 'Mehmet Kaya',
        'testimonial-3-company': 'EcoTech Industries',
        'testimonial-3-role': 'CTO',
        'testimonial-3-content': 'Durch das Blockchain-unterstützte transparente System können unsere Kunden unseren Zertifikat sehr einfach überprüfen. Das erzeugt Vertrauen.',
        'why-us-standards-title': 'Konformität mit globalen Kohlenstoffstandards',
        'standard-1-title': 'Gold Standard',
        'standard-1-desc': 'Höchste Qualität an Kohlenstoffstandard',
        'standard-1-status': 'Konform',
        'standard-2-title': 'Verra VCS',
        'standard-2-desc': 'Verra VCS-Verifizierung',
        'standard-2-status': 'Zugelassen',
        'standard-3-title': 'ISO 14001',
        'standard-3-desc': 'Umweltmanagementsystem-Standard',
        'standard-3-status': 'Zertifiziert',
        'why-us-blockchain-title': 'Blockchain-unterstütztes transparentes Registrierungssystem',
        'why-us-blockchain-desc': 'Alle unsere Kohlenstoffausgleichsprozesse werden mit Blockchain-Technologie aufgezeichnet. Dadurch sind alle Prozesse transparent, überprüfbar und nicht änderbar.',
        'why-us-blockchain-feature-1': 'Echtzeit-Tracking',
        'why-us-blockchain-feature-2': 'Unveränderliche Aufzeichnungen',
        'why-us-blockchain-feature-3': 'Öffentliche Verifizierung',
        'why-us-recent-transactions': 'Letzte Transaktionen',
        
        // Certificate Form
        'form-subtitle': 'Machen Sie Ihre Website CO₂-neutral und erhalten Sie Ihr Zertifikat',
        'form-application-type': 'Antragstyp',
        'form-type-personal': 'Persönlich',
        'form-type-personal-desc': 'Individuelle Websites',
        'form-type-corporate': 'Unternehmen',
        'form-type-corporate-desc': 'Unternehmenswebsites',
        'form-name': 'Vor- und Nachname',
        'form-authority-person': 'Bevollmächtigte Person',
        'form-name-placeholder': 'Ihr Vor- und Nachname',
        'form-authority-placeholder': 'Name der bevollmächtigten Person',
        'form-email': 'E-Mail',
        'form-company-name': 'Firmenname',
        'form-company-placeholder': 'Ihr Firmenname',
        'form-sector': 'Branche',
        'form-sector-select': 'Branche auswählen',
        'form-sector-tech': 'Technologie',
        'form-sector-ecommerce': 'E-Commerce',
        'form-sector-finance': 'Finanzen',
        'form-sector-health': 'Gesundheit',
        'form-sector-education': 'Bildung',
        'form-sector-tourism': 'Tourismus',
        'form-sector-media': 'Medien',
        'form-sector-other': 'Andere',
        'form-website': 'Website-Adresse',
        'form-phone': 'Telefon',
        'form-message': 'Zusätzliche Notizen',
        'form-message-placeholder': 'Zusätzliche Informationen, spezielle Anfragen oder Fragen...',
        'form-submitting': 'Wird gesendet...',
        'form-submit': 'Antrag senden',
        'form-success-title': 'Ihr Antrag wurde erhalten!',
        'form-success-message': 'Ihr Zertifikatsantrag wurde erfolgreich gespeichert. Unser Expertenteam wird sich in Kürze mit Ihnen in Verbindung setzen und Ihnen detaillierte Informationen über den Prozess geben.',
        'form-next-steps': 'Nächste Schritte',
        'form-step-1': 'Sie erhalten eine Bestätigungs-E-Mail',
        'form-step-2': 'Unser Experte wird Sie innerhalb von 24 Stunden anrufen',
        'form-step-3': 'Ihre Website wird analysiert',
        'form-step-4': 'Der CO₂-neutrale Prozess wird gestartet',
        'form-new-application': 'Neuer Antrag',
        'form-benefits-title': 'Vorteile',
        'form-benefit-1-title': 'Umweltfreundliches Image',
        'form-benefit-1-desc': 'Zeigen Sie Ihren Kunden Ihr Engagement für Nachhaltigkeit',
        'form-benefit-2-title': 'SEO-Vorteil',
        'form-benefit-2-desc': 'Erhalten Sie bessere Rankings in Suchmaschinen',
        'form-benefit-3-title': 'Kosteneinsparungen',
        'form-benefit-3-desc': 'Sparen Sie mit energieeffizienter Infrastruktur',
        'form-process-title': 'Prozess',
        'form-process-1': 'CO₂-Fußabdruck-Analyse',
        'form-process-2': 'Nachhaltiges Hosting-Setup',
        'form-process-3': 'CO₂-Ausgleich',
        'form-process-4': 'Zertifikatslieferung',
        
        // Video Section
        'video-title': 'Warum ist CO₂ Neutrality wichtig?',
        'video-subtitle': 'Verstehen Sie die Umweltbelastung der digitalen Welt und entdecken Sie, warum CO₂ Neutrality kritisch ist',
        'video-video-title': 'CO₂ Neutral Websites',
        'video-duration': '3:45 Minuten',
        'video-quality': 'HD-Qualität',
        'video-content-title': 'Der versteckte CO₂-Fußabdruck der digitalen Welt',
        'video-content-desc': 'Das Internet ist einer der größten Stromverbraucher der Welt. Jeder Website-Besuch, jede E-Mail-Sendung, jede Suchanfrage verursacht CO₂-Emissionen.',
        'video-stat-1': 'Globale CO₂-Emissionen',
        'video-stat-2': 'Tonnen Einsparung',
        'video-stat-3': 'Energieeinsparung',
        
        // Tree Calculator
        'tree-calculator-title': 'Wie viele Bäume möchten Sie im Wald pflanzen?',
        'tree-calculator-slider': 'Schieberegler',
        'tree-calculator-co2': 'CO2-Menge',
        'tree-calculator-visitor': 'Besucher',
        'tree-calculator-samples': 'Beispiele',
        'tree-calculator-trees': 'Bäume',
        'tree-calculator-co2-placeholder': 'CO₂ (Tonnen)',
        'tree-calculator-visitor-placeholder': 'Monatliche Besucherzahl',
        'tree-calculator-visitor-note': 'Für je 1000 Besucher wird 1 Baum empfohlen.',
        'tree-calculator-sample-1': '• 1 Flugticket (Istanbul-Berlin): 2 Bäume',
        'tree-calculator-sample-2': '• 1 Jahr durchschnittliche Website: 5 Bäume',
        'tree-calculator-sample-3': '• 1 Laptop: 3 Bäume',
        'tree-calculator-unit-price': 'Einheitspreis €8/Baum',
        'tree-calculator-cta': 'SOFORT BÄUME PFLANZEN',
        
        // Visitor Boost
        'visitor-boost-title': 'Machen Sie Ihre Website CO₂-neutral,',
        'visitor-boost-increase': '+30% Besuchersteigerung',
        'visitor-boost-achieve': 'Erreichen Sie es!',
        'visitor-boost-description': 'CO₂-neutrale Websites ziehen umweltbewusste Nutzer an und führen zu über 30% mehr Besuchern. Werden Sie jetzt CO₂-neutral und tragen Sie sowohl zur Umwelt als auch zu Ihrem Geschäft bei!',
        
        // Big Brands Zero Carbon
        'big-brands-title': 'Große Marken auf dem Weg zu 0 CO₂-Fußabdruck',
        'big-brands-subtitle': 'Die größten Unternehmen der Welt entscheiden sich für CO₂-Neutralität. Diese Marken, die in Sachen Nachhaltigkeit führend sind, setzen in der digitalen Welt ein Beispiel.',
        'brand-google-desc': 'Google ist seit 2007 CO₂-neutral und strebt an, bis 2030 seinen CO₂-Fußabdruck vollständig zu eliminieren.',
        'brand-microsoft-desc': 'Microsoft ist seit 2012 CO₂-neutral und hat sich verpflichtet, bis 2030 CO₂-negativ zu werden.',
        'brand-amazon-desc': 'Amazon strebt bis 2040 Netto-Null-Kohlenstoff in allen seinen Operationen an und investiert massiv in erneuerbare Energien.',
        'brand-tesla-desc': 'Tesla zielt darauf ab, den CO₂-Fußabdruck durch Elektrofahrzeuge und Energielösungen zu minimieren.',
        'brand-visit-sustainability': 'Besuchen Sie die Nachhaltigkeitsseite →',
        'big-brands-cta': 'Werden auch Sie CO₂-neutral und führen Sie in Sachen Nachhaltigkeit!',
        
        // Cart and Checkout
        'add-to-cart': 'In den Warenkorb',
        'cart-hide': 'Verstecken',
        'cart-show': 'Anzeigen',
        'cart-items': 'Warenkorb Artikel',
        'cart-total': 'Gesamt',
        'proceed-to-checkout': 'Zur Kasse',
        'checkout-title': 'Kasse',
        'checkout-no-order': 'Keine Bestellung gefunden',
        'back-to-home': 'Zurück zur Startseite',
        'order-summary': 'Bestellübersicht',
        'order-id': 'Bestellnummer',
        'total-amount': 'Gesamtbetrag',
        'payment-method': 'Zahlungsmethode',
        'bank-transfer': 'Banküberweisung',
        'bank-details': 'Bankdaten',
        'iban': 'IBAN',
        'bic': 'BIC',
        'account-holder': 'Kontoinhaber',
        'address': 'Adresse',
        'reference': 'Verwendungszweck',
        'important-notes': 'Wichtige Hinweise',
        'note-1': 'Vergessen Sie nicht, die Referenznummer bei der Zahlung anzugeben',
        'note-2': 'Die Zahlung wird innerhalb von 1-3 Werktagen bearbeitet',
        'note-3': 'Nach Bestätigung der Bestellung erhalten Sie eine E-Mail-Benachrichtigung',
        'confirm-order': 'Bestellung bestätigen',
        'order-confirmed': 'Bestellung bestätigt!',
        'order-confirmed-desc': 'Ihre Bestellung wurde erfolgreich aufgenommen. Die Zahlungsinformationen finden Sie unten.',
        'payment-instructions': 'Zahlungsanweisungen',
        'download-invoice': 'Rechnung herunterladen',
        
        // Live Chat Widget
        'chat-title': 'Live Support',
        'chat-placeholder': 'Schreiben Sie Ihre Nachricht...',
        'chat-greeting': 'Hallo! Wie kann ich Ihnen helfen?',
        'chat-reply-1': 'Ich kann Ihnen dabei helfen!',
        'chat-reply-2': 'Können Sie Ihre Frage genauer erläutern?',
        'chat-reply-3': 'Ich werde mich so schnell wie möglich bei Ihnen melden.',
        'chat-reply-4': 'Möchten Sie weitere Informationen erhalten?',
        'chat-ai-assistant': 'KI-Assistent',
        
        // Tax Advantage for Companies
        'casino-tax-advantage-badge': '100% Steuervorteil',
        'casino-tax-advantage-title': 'Sichern Sie Ihrem Unternehmen 100% Steuervorteil',
        'casino-tax-advantage-subtitle': 'Unternehmen profitieren von erheblichen Steuervorteilen durch CO₂-neutrale Zertifizierung – jetzt für Ihr Unternehmen nutzen',
        'casino-tax-advantage-80-title': '100% Steuerersparnis',
        'casino-tax-advantage-80-desc': 'Ihr Unternehmen kann bis zu 100% Steuervorteile erhalten, wenn es sich für eine CO₂-neutrale Zertifizierung entscheidet.',
        'casino-tax-advantage-benefits-title': 'Große Vorteile',
        'casino-tax-advantage-benefits-desc': 'Neben Steuervorteilen profitieren Sie auch von verbessertem Image, höherer Kundenbindung und nachhaltigem Wachstum.',
        'casino-tax-advantage-how-title': 'Wie funktioniert es?',
        'casino-tax-advantage-step-1': 'Beantragen Sie Ihr CO₂-neutrales Zertifikat für Ihr Unternehmen',
        'casino-tax-advantage-step-2': 'Erhalten Sie automatisch Zugang zu Steuervorteilen von bis zu 100%',
        'casino-tax-advantage-step-3': 'Profitieren Sie von langfristigen finanziellen und umweltbezogenen Vorteilen',
        'casino-tax-advantage-cta': 'Jetzt Steuervorteil sichern',
        
        // Carbon Advantage for Companies
        'casino-carbon-advantage-title': 'CO₂-neutrales Zertifikat – Große Vorteile für Ihr Unternehmen',
        'casino-carbon-advantage-subtitle': 'Entdecken Sie, wie ein CO₂-neutrales Zertifikat Ihrem Unternehmen erhebliche steuerliche und wirtschaftliche Vorteile bringt',
        'casino-carbon-advantage-1-title': 'Steuerliche Vorteile',
        'casino-carbon-advantage-1-desc': 'Profitieren Sie von bis zu 100% Steuervorteilen durch CO₂-neutrale Zertifizierung',
        'casino-carbon-advantage-2-title': 'Verbessertes Image',
        'casino-carbon-advantage-2-desc': 'Zeigen Sie Ihr Engagement für Nachhaltigkeit und gewinnen Sie das Vertrauen Ihrer Kunden',
        'casino-carbon-advantage-3-title': 'Wettbewerbsvorteil',
        'casino-carbon-advantage-3-desc': 'Unterscheiden Sie sich von der Konkurrenz mit nachhaltigen Geschäftspraktiken',
        'casino-carbon-advantage-main-title': 'Warum CO₂-neutrales Zertifikat für Ihr Unternehmen?',
        'casino-carbon-advantage-main-desc': 'Unternehmen mit CO₂-neutralem Zertifikat genießen erhebliche Vorteile – darunter große Steuerersparnisse und ein verbessertes Markenimage.',
        'casino-carbon-advantage-benefit-1': 'Bis zu 100% Steuervorteile für Ihr Unternehmen',
        'casino-carbon-advantage-benefit-2': 'Verbessertes Markenimage und Kundenvertrauen',
        'casino-carbon-advantage-benefit-3': 'Langfristige finanzielle und umweltbezogene Vorteile',
        'casino-carbon-advantage-tax-savings': 'Steuerersparnis',
        'casino-carbon-advantage-cta': 'Jetzt Steuervorteil sichern',
      }
    },
    en: {
      code: 'en',
      name: 'English',
      flag: '🇬🇧',
      translations: {
        // Navigation
        'about': 'About',
        'services': 'Services',
        'certificate-verify': 'Verify Certificate',
        'add-badge': 'Add CO₂ Badge',
        'get-certificate': 'Get Certificate',
        
        // Hero Section
        'hero-title': 'Step into a CO₂ Neutral Future',
        'hero-subtitle': 'Reduce your CO₂ emissions, make your digital assets neutral',
        'hero-title-2': 'Zero Carbon in the Digital Age',
        'hero-subtitle-2': 'Make your website environmentally friendly and invest in the future',
        'hero-title-3': 'Meet Sustainable Technology',
        'hero-subtitle-3': 'Create a difference with sustainable digital solutions',
        'hero-title-4': 'Take Action Today for the Future',
        'hero-subtitle-4': 'Reduce your carbon footprint and contribute to the environment',
        'hero-title-5': 'Join Your Website for a Better Environment',
        'hero-subtitle-5': 'Support the environment with your carbon neutral certificate',
        'hero-title-6': 'Zero Emission, Zero Worries',
        'hero-subtitle-6': 'Make your digital assets neutral',
        'hero-title-7': 'For a Cleaner Internet',
        'hero-subtitle-7': 'Make your website carbon neutral and create a difference',
        'hero-title-8': 'Environmentally Friendly Digital Transformation',
        'hero-subtitle-8': 'Your sustainable journey begins here',
        'hero-title-9': 'Trust a Carbon Neutral Certificate',
        'hero-subtitle-9': 'Show your visitors your environmental awareness',
        'hero-title-10': 'Reduce Your Digital Footprint',
        'hero-subtitle-10': 'Contribute to the environment with every click',
        'hero-cta-primary': 'Become CO₂ Neutral Now',
        'hero-cta-secondary': 'Get Certificate',
        'hero-stats-experience': 'Years of Experience',
        'hero-stats-sites': 'Carbon Neutral Websites',
        'hero-stats-savings': 'CO₂ Savings in Tons',
        
        // About Section
        'about-title': 'About',
        'about-subtitle': 'Operating in Berlin since 2008, we provide leading sustainable technology solutions.',
        'about-history-title': 'Company History',
        'about-history-text': 'Our company was founded in Berlin in 2008 with the aim of minimizing the environmental impact of the digital age. With over 15 years of experience, we have brought thousands of websites to carbon neutrality and have become the leading company in sustainable technology solutions.',
        'about-mission-title': 'Our Mission',
        'about-mission-text': 'We propose reducing the environmental impact of digital assets to zero to create a sustainable future. By ensuring that every website is carbon neutral, we contribute to achieving global climate goals.',
        'about-vision-title': 'Our Vision',
        'about-vision-text': 'A world where all digital platforms are carbon neutral, and technology stands in harmony with nature. A sustainable digital ecosystem where every byte of the internet is produced with environmental concerns.',
        'about-technologies-title': 'Carbon Neutral Technologies',
        'about-tech-1': 'Sustainable hosting and cloud infrastructure',
        'about-tech-2': 'Energy-efficient code optimization',
        'about-tech-3': 'Carbon credit integration',
        'about-tech-4': 'Blockchain-supported transparent registration system',
        'about-tech-5': 'Real-time emission tracking',
        'about-achievement-1-title': 'Gold Standard',
        'about-achievement-1-text': 'Compliance with global carbon standards',
        'about-achievement-2-title': 'Verra Certificate',
        'about-achievement-2-text': 'International carbon verification',
        'about-achievement-3-title': 'ISO 14001',
        'about-achievement-3-text': 'Environmental management system',
        
        // Services
        'services-title': 'Our Services',
        'services-subtitle': 'All solutions to make your website carbon neutral',
        'service-co2-web': 'CO₂ Neutral Website Services',
        'service-co2-web-desc': 'With our infrastructure and hosting services, we bring your website to full carbon neutrality.',
        'service-co2-web-feature-1': 'Sustainable Hosting Infrastructure',
        'service-co2-web-feature-2': 'Energy-efficient Servers',
        'service-co2-web-feature-3': 'Automated Carbon Offset',
        'service-co2-web-feature-4': '24/7 Technical Support',
        'service-calculator': 'Carbon Footprint Calculator',
        'service-calculator-desc': 'Calculate your actual carbon footprint and develop strategies for reduction.',
        'service-calculator-feature-1': 'Detailed Emission Analysis',
        'service-calculator-feature-2': 'Page Load Time Optimization',
        'service-calculator-feature-3': 'Energy Consumption Reports',
        'service-calculator-feature-4': 'Comparative Performance',
        'service-certificate': 'CO₂ Certification',
        'service-certificate-desc': 'Get carbon neutral certificates that comply with international standards and are verifiable.',
        'service-certificate-feature-1': 'Gold Standard Compliance',
        'service-certificate-feature-2': 'Verra Verification',
        'service-certificate-feature-3': 'Blockchain Registration System',
        'service-certificate-feature-4': 'Download PDF Certificate',
        'service-exchange': 'CO₂ Exchange Connection',
        'service-exchange-desc': 'Earn additional income by trading your carbon credits on international exchanges.',
        'service-exchange-feature-1': 'Real-time Price Tracking',
        'service-exchange-feature-2': 'Automated Buy-Sell',
        'service-exchange-feature-3': 'Portfolio Management',
        'service-exchange-feature-4': 'Market Analysis',
        'services-how-it-works': 'How does it work?',
        'services-step-1-title': 'Analysis',
        'services-step-1-desc': 'We analyze your carbon footprint',
        'services-step-2-title': 'Optimization',
        'services-step-2-desc': 'We increase energy efficiency',
        'services-step-3-title': 'Offset',
        'services-step-3-desc': 'We balance the remaining emissions with carbon credits',
        'services-step-4-title': 'Certificate',
        'services-step-4-desc': 'You receive your carbon neutral certificate',
        
        // Certificate Verification
        'verification-title': 'Certificate Verification System',
        'verification-subtitle': 'Verify validity by entering your certificate number',
        'verification-placeholder': 'Enter certificate code (e.g.: CO2N-2024-001)',
        'verification-button': 'Verify',
        'verification-loading': 'Verifying...',
        'verification-valid': 'Certificate Valid',
        'verification-invalid': 'Certificate Invalid',
        'cert-verification-title': 'Certificate Verification System',
        'cert-verification-subtitle': 'Verify validity by entering your certificate number',
        'cert-verification-placeholder': 'Enter certificate code (e.g.: CO2N-2024-001)',
        'cert-verification-verify': 'Verify',
        'cert-verification-verifying': 'Verifying...',
        'cert-verification-valid': 'Certificate Valid',
        'cert-verification-invalid': 'Certificate Invalid',
        'cert-verification-company': 'Company Name',
        'cert-verification-website': 'Website',
        'cert-verification-issued-date': 'Issued Date',
        'cert-verification-expiry-date': 'Expiry Date',
        'cert-verification-carbon-offset': 'Carbon Offset',
        'cert-verification-certificate-id': 'Certificate ID',
        'cert-verification-download-pdf': 'Download PDF',
        'cert-verification-blockchain-verified': 'Blockchain verification completed',
        'cert-verification-example-codes': 'Example Certificate Codes',
        'cert-verification-valid-cert': 'Valid Certificate',
        'cert-verification-enter-code': 'Please enter a certificate code',
        'cert-verification-invalid-code': 'Invalid certificate code',
        'cert-verification-not-found': 'Certificate not found',
        'cert-truja-carbon-offset': 'Carbon neutral certificate',
        'cert-neckar-carbon-offset': '250 trees planted',
        'cert-ish-carbon-offset': '12,000 tonnes of CO₂ neutralised',
        'cert-enucuzfly-carbon-offset': '850 tonnes of CO₂ neutralised',
        'global-partners-title': 'Our Global Carbon Footprint Partners',
        
        // Certificate Verification Page
        'cert-page-title': 'Certificate Verification System',
        'cert-page-subtitle': 'Verify validity by entering your certificate number. Blockchain-supported secure verification system to instantly check your certificates.',
        'cert-page-stats-verified': 'Verified Certificates',
        'cert-page-stats-accuracy': 'Accuracy Rate',
        'cert-page-stats-time': 'Average Time',
        'cert-page-verify-title': 'Verify Certificate',
        'cert-page-valid-message': 'This certificate is active and valid',
        'cert-page-status': 'Status',
        'cert-page-verification-count': 'Verification Count',
        'cert-page-features-title': 'Features',
        'cert-page-feature-1-title': 'Blockchain Security',
        'cert-page-feature-1-desc': 'All certificates are securely stored on the blockchain',
        'cert-page-feature-2-title': 'Instant Verification',
        'cert-page-feature-2-desc': 'Certificate validity is checked instantly',
        'cert-page-feature-3-title': 'Global Access',
        'cert-page-feature-3-desc': 'Worldwide accessible verification system',
        'cert-page-recent-title': 'Recent Verifications',
        'cert-page-recent-verified': 'Verified',
        'cert-page-status-active': 'Active',
        'cert-pdf-certificate-of': 'Certificate',
        'cert-pdf-footer-note': 'This certificate has been verified with blockchain technology and can be checked online for validity.',
        
        // Add Badge
        'badge-title': 'Add CO₂ Neutral Badge',
        'badge-subtitle': 'Add a customizable CO₂ neutral badge to your website',
        'badge-language': 'Language Selection',
        'badge-text': 'This site is carbon neutral',
        
        // Footer
        'footer-company-desc': 'Sürdürülebilir teknoloji çözümleri sunan öncü kuruluş.',
        'footer-carbon-neutral': 'This site is carbon neutral',
        'footer-certificate-no': 'Certificate No: CO2N-2024-001',
        'footer-quick-links': 'Quick Links',
        'footer-contact': 'Contact',
        'footer-address': 'Berlin, Germany',
        'footer-social-media': 'Social Media',
        'footer-copyright': '© 2025 CO2 Neutral. All rights reserved.',
        'footer-privacy': 'Privacy Policy',
        'footer-cookies': 'Cookie Policy',
        'footer-terms': 'Terms of Use',
        'why-us-title': 'Why Us?',
        
        // Contact Page
        'contact-page-title': 'Contact',
        'contact-page-subtitle': 'Get in touch with us. We are happy to help you.',
        'contact-form-title': 'Send Message',
        'contact-form-name': 'Full Name',
        'contact-form-name-placeholder': 'Your full name',
        'contact-form-email': 'Email',
        'contact-form-email-placeholder': 'Your email address',
        'contact-form-subject': 'Subject',
        'contact-form-subject-placeholder': 'Subject of your message',
        'contact-form-message': 'Message',
        'contact-form-message-placeholder': 'Write your message here...',
        'contact-form-submit': 'Send Message',
        'contact-success-title': 'Your message has been sent!',
        'contact-success-message': 'We will get back to you as soon as possible.',
        'contact-new-message': 'New Message',
        'contact-business-hours': 'Business Hours',
        'contact-monday-friday': 'Monday - Friday',
        'contact-saturday': 'Saturday',
        'contact-sunday': 'Sunday',
        'contact-closed': 'Closed',
        'contact-quick-contact': 'Quick Contact',
        'contact-quick-contact-desc': 'For emergencies, you can call us or send an email.',
        'contact-map-title': 'Location',
        'contact-map-placeholder': 'Loading map...',
        
        // Why Us Section
        'why-us-subtitle': '15 years of experience and thousands of successful projects make us the leading company in sustainable technology solutions.',
        'why-us-stats-customers': 'Happy Customers',
        'why-us-stats-certificates': 'Certificates',
        'why-us-stats-countries': 'Countries',
        'why-us-stats-rating': 'Customer Rating',
        'why-us-testimonials-title': 'Customer Reviews',
        'testimonial-1-name': 'Ahmet Yılmaz',
        'testimonial-1-company': 'TechCorp GmbH',
        'testimonial-1-role': 'CEO',
        'testimonial-1-content': 'With CO2 Neutral, we made our website carbon neutral. Our customers now know they are working with an environmentally friendly company, and this gives us confidence.',
        'testimonial-2-name': 'Maria Schmidt',
        'testimonial-2-company': 'GreenStart Solutions',
        'testimonial-2-role': 'Marketing Director',
        'testimonial-2-content': 'The certification process was very easy. The technical team helped us at every step and in the end we had a fantastic carbon neutral website.',
        'testimonial-3-name': 'Mehmet Kaya',
        'testimonial-3-company': 'EcoTech Industries',
        'testimonial-3-role': 'CTO',
        'testimonial-3-content': 'Thanks to the blockchain-supported transparent system, our customers can easily verify our certificate. This creates trust.',
        'why-us-standards-title': 'Compliance with Global Carbon Standards',
        'standard-1-title': 'Gold Standard',
        'standard-1-desc': 'Highest quality carbon credit standard',
        'standard-1-status': 'Compliant',
        'standard-2-title': 'Verra VCS',
        'standard-2-desc': 'Voluntary Carbon Standard verification',
        'standard-2-status': 'Approved',
        'standard-3-title': 'ISO 14001',
        'standard-3-desc': 'Environmental management system standard',
        'standard-3-status': 'Certified',
        'why-us-blockchain-title': 'Blockchain-Supported Transparent Registration System',
        'why-us-blockchain-desc': 'All our carbon offset processes are recorded with blockchain technology. This makes all processes transparent, verifiable and unchangeable.',
        'why-us-blockchain-feature-1': 'Real-time tracking',
        'why-us-blockchain-feature-2': 'Immutable records',
        'why-us-blockchain-feature-3': 'Public verification',
        'why-us-recent-transactions': 'Recent Transactions',
        
        // Certificate Form
        'form-subtitle': 'Make your website carbon neutral and get your certificate',
        'form-application-type': 'Application Type',
        'form-type-personal': 'Personal',
        'form-type-personal-desc': 'Individual websites',
        'form-type-corporate': 'Corporate',
        'form-type-corporate-desc': 'Company websites',
        'form-name': 'Full Name',
        'form-authority-person': 'Authorized Person',
        'form-name-placeholder': 'Your full name',
        'form-authority-placeholder': 'Authorized person name',
        'form-email': 'Email',
        'form-company-name': 'Company Name',
        'form-company-placeholder': 'Your company name',
        'form-sector': 'Sector',
        'form-sector-select': 'Select sector',
        'form-sector-tech': 'Technology',
        'form-sector-ecommerce': 'E-commerce',
        'form-sector-finance': 'Finance',
        'form-sector-health': 'Healthcare',
        'form-sector-education': 'Education',
        'form-sector-tourism': 'Tourism',
        'form-sector-media': 'Media',
        'form-sector-other': 'Other',
        'form-website': 'Website Address',
        'form-phone': 'Phone',
        'form-message': 'Additional Notes',
        'form-message-placeholder': 'Additional information, special requests or questions...',
        'form-submitting': 'Submitting...',
        'form-submit': 'Submit Application',
        'form-success-title': 'Your Application Received!',
        'form-success-message': 'Your certificate application has been successfully saved. Our expert team will contact you shortly and provide detailed information about the process.',
        'form-next-steps': 'Next Steps',
        'form-step-1': 'You will receive a confirmation email',
        'form-step-2': 'Our expert will call you within 24 hours',
        'form-step-3': 'Your website will be analyzed',
        'form-step-4': 'Carbon neutral process will be initiated',
        'form-new-application': 'New Application',
        'form-benefits-title': 'Benefits',
        'form-benefit-1-title': 'Environmentally Friendly Image',
        'form-benefit-1-desc': 'Show your customers your commitment to sustainability',
        'form-benefit-2-title': 'SEO Advantage',
        'form-benefit-2-desc': 'Get better rankings in search engines',
        'form-benefit-3-title': 'Cost Savings',
        'form-benefit-3-desc': 'Save with energy-efficient infrastructure',
        'form-process-title': 'Process',
        'form-process-1': 'Carbon footprint analysis',
        'form-process-2': 'Green hosting setup',
        'form-process-3': 'Carbon offset',
        'form-process-4': 'Certificate delivery',
        
        // Video Section
        'video-title': 'Why is CO₂ Neutrality Important?',
        'video-subtitle': 'Understand the environmental impact of the digital world and discover why carbon neutrality is critical',
        'video-video-title': 'CO₂ Neutral Websites',
        'video-duration': '3:45 minutes',
        'video-quality': 'HD Quality',
        'video-content-title': 'The Hidden Carbon Footprint of the Digital World',
        'video-content-desc': 'The internet is one of the world\'s largest electricity consumers. Every website visit, every email sent, every search query causes carbon emissions.',
        'video-stat-1': 'Global CO₂ Emissions',
        'video-stat-2': 'Tons Saved',
        'video-stat-3': 'Energy Savings',
        
        // Tree Calculator
        'tree-calculator-title': 'How many trees do you want to plant in the forest?',
        'tree-calculator-slider': 'Slider',
        'tree-calculator-co2': 'CO2 Amount',
        'tree-calculator-visitor': 'Visitors',
        'tree-calculator-samples': 'Examples',
        'tree-calculator-trees': 'Trees',
        'tree-calculator-co2-placeholder': 'CO₂ (tons)',
        'tree-calculator-visitor-placeholder': 'Monthly Visitor Count',
        'tree-calculator-visitor-note': '1 tree is recommended for every 1000 visitors.',
        'tree-calculator-sample-1': '• 1 flight ticket (Istanbul-Berlin): 2 trees',
        'tree-calculator-sample-2': '• 1 year average website: 5 trees',
        'tree-calculator-sample-3': '• 1 laptop: 3 trees',
        'tree-calculator-unit-price': 'Unit Price €8/Tree',
        'tree-calculator-cta': 'BECOME BREATH NOW',
        
        // Visitor Boost
        'visitor-boost-title': 'Make Your Site Carbon Neutral,',
        'visitor-boost-increase': '+30% Visitor Increase',
        'visitor-boost-achieve': 'Achieve It!',
        'visitor-boost-description': 'Carbon neutral websites attract environmentally conscious users and provide over 30% more visitors. Become carbon neutral now and contribute to both nature and your business!',
        
        // Big Brands Zero Carbon
        'big-brands-title': 'Big Brands on the Path to 0 Carbon Footprint',
        'big-brands-subtitle': 'The world\'s largest companies are choosing carbon neutrality. These brands, leading in sustainability, set an example in the digital world.',
        'brand-google-desc': 'Google has been carbon neutral since 2007 and aims to completely eliminate its carbon footprint by 2030.',
        'brand-microsoft-desc': 'Microsoft has been carbon neutral since 2012 and has committed to becoming carbon negative by 2030.',
        'brand-amazon-desc': 'Amazon aims for net-zero carbon across all its operations by 2040 and makes massive renewable energy investments.',
        'brand-tesla-desc': 'Tesla aims to minimize carbon footprint through electric vehicles and energy solutions.',
        'brand-visit-sustainability': 'Visit sustainability page →',
        'big-brands-cta': 'Become carbon neutral too and lead in sustainability!',
        
        // Cart and Checkout
        'add-to-cart': 'Add to Cart',
        'cart-hide': 'Hide',
        'cart-show': 'Show',
        'cart-items': 'Cart Items',
        'cart-total': 'Total',
        'proceed-to-checkout': 'Proceed to Checkout',
        'checkout-title': 'Checkout',
        'checkout-no-order': 'No order found',
        'back-to-home': 'Back to Home',
        'order-summary': 'Order Summary',
        'order-id': 'Order ID',
        'total-amount': 'Total Amount',
        'payment-method': 'Payment Method',
        'bank-transfer': 'Bank Transfer',
        'bank-details': 'Bank Details',
        'iban': 'IBAN',
        'bic': 'BIC',
        'account-holder': 'Account Holder',
        'address': 'Address',
        'reference': 'Reference',
        'important-notes': 'Important Notes',
        'note-1': 'Don\'t forget to include the reference number when making payment',
        'note-2': 'Payment will be processed within 1-3 business days',
        'note-3': 'You will be notified by email after order confirmation',
        'confirm-order': 'Confirm Order',
        'order-confirmed': 'Order Confirmed!',
        'order-confirmed-desc': 'Your order has been successfully received. Payment information is provided below.',
        'payment-instructions': 'Payment Instructions',
        'download-invoice': 'Download Invoice',
        
        // Live Chat Widget
        'chat-title': 'Live Support',
        'chat-placeholder': 'Write your message...',
        'chat-greeting': 'Hello! How can I help you?',
        'chat-reply-1': 'I can help you with that!',
        'chat-reply-2': 'Can you elaborate on your question?',
        'chat-reply-3': 'I will get back to you as soon as possible.',
        'chat-reply-4': 'Would you like to get more information?',
        'chat-ai-assistant': 'AI Assistant',
        
        // Tax Advantage for Companies
        'casino-tax-advantage-badge': '100% Tax Advantage',
        'casino-tax-advantage-title': 'Secure 100% Tax Advantage for Your Company',
        'casino-tax-advantage-subtitle': 'Companies benefit from significant tax advantages through CO₂ neutral certification – start saving tax for your business today',
        'casino-tax-advantage-80-title': '100% Tax Savings',
        'casino-tax-advantage-80-desc': 'Your company can receive up to 100% tax advantages by choosing CO₂ neutral certification.',
        'casino-tax-advantage-benefits-title': 'Major Benefits',
        'casino-tax-advantage-benefits-desc': 'In addition to tax advantages, you also benefit from improved image, higher customer loyalty, and sustainable growth.',
        'casino-tax-advantage-how-title': 'How does it work?',
        'casino-tax-advantage-step-1': 'Apply for your CO₂ neutral certificate for your company',
        'casino-tax-advantage-step-2': 'Automatically gain access to tax advantages of up to 100%',
        'casino-tax-advantage-step-3': 'Benefit from long-term financial and environmental advantages',
        'casino-tax-advantage-cta': 'Secure Your Tax Advantage Now',
        
        // Carbon Advantage for Companies
        'casino-carbon-advantage-title': 'Carbon Neutral Certificate – Major Benefits for Your Company',
        'casino-carbon-advantage-subtitle': 'Discover how a carbon neutral certificate brings significant tax and economic advantages to your company',
        'casino-carbon-advantage-1-title': 'Tax Benefits',
        'casino-carbon-advantage-1-desc': 'Benefit from up to 100% tax advantages through carbon neutral certification',
        'casino-carbon-advantage-2-title': 'Improved Image',
        'casino-carbon-advantage-2-desc': 'Show your commitment to sustainability and gain customer trust',
        'casino-carbon-advantage-3-title': 'Competitive Advantage',
        'casino-carbon-advantage-3-desc': 'Stand out from the competition with sustainable business practices',
        'casino-carbon-advantage-main-title': 'Why a Carbon Neutral Certificate for Your Company?',
        'casino-carbon-advantage-main-desc': 'Companies with a carbon neutral certificate enjoy significant advantages – including major tax savings and an improved brand image.',
        'casino-carbon-advantage-benefit-1': 'Up to 100% tax advantages for your company',
        'casino-carbon-advantage-benefit-2': 'Improved brand image and customer trust',
        'casino-carbon-advantage-benefit-3': 'Long-term financial and environmental benefits',
        'casino-carbon-advantage-tax-savings': 'Tax Savings',
        'casino-carbon-advantage-cta': 'Secure Your Tax Advantage Now',
      }
    }
  };

  const changeLanguage = (langCode) => {
    setCurrentLanguage(langCode);
    // Dil değişikliğini localStorage'a kaydet
    localStorage.setItem('preferredLanguage', langCode);
  };

  const t = (key) => {
    const currentLang = languages[currentLanguage];
    return currentLang?.translations[key] || key;
  };

  const value = {
    currentLanguage,
    changeLanguage,
    t,
    languages: Object.values(languages)
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}; 